import { useQuery, useMutation, useQueryClient  } from "@tanstack/react-query";
import { User, PlusCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChildModal from './ChildModal';
import PassportSection from './PassportSection';
import PassportResultModal from './PassportResultModal';
import PassportModal from './PassportModal';
import Portal from '../../../../components/common/Portal';

const ChildrenSection = () => {
    const API_URL = import.meta.env.VITE_API_URL;
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [children, setChildren] = useState([]);
    const [passport, setPassport] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPassportModalOpen, setIsPassportModalOpen] = useState(false);
    const [isPassportResultModalOpen, setIsPassportResultModalOpen] = useState(false);
    const [selectedChild, setSelectedChild] = useState();

    const { data : memberData } = useQuery({
        queryKey: ['member'],
        queryFn: async () => {
            const response = await fetch(`${API_URL}/bbs/ajax.get_member.php`);
            return response.json();
        }
    });
    const member = memberData?.data ?? null;

    const { data : passportList } = useQuery({
        queryKey: ['passportList'],
        queryFn: async () => {
            const response = await fetch(`${API_URL}/bbs/ajax.get_passport_list.php`);
            return response.json();
        }
    });
    const passportData = passportList?.data ?? null;

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const params = new URLSearchParams();
            params.append("ch_id", id);

            const response = await fetch(`${API_URL}/bbs/ajax.delete_child.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: params,
                credentials: "include",
            });

            if (!response.ok) {
                throw new Error("네트워크 오류");
            }

            return response.json();
        },
        onSuccess: (response) => {
            if(!response.success) {
                switch (response.code) {
                    case "INVALID_REQUEST":
                        alert("필수 필드값이 입력되지 않았습니다.");
                        break;
                    default :
                        alert("알 수 없는 오류가 발생했습니다.");
                        break;
                }
                return;
            }

            if(!response.data) {
                alert("알 수 없는 오류가 발생했습니다.");
                return;
            }

            if(response.data.memberId) {
                alert("삭제되었습니다.");
                queryClient.invalidateQueries({queryKey: ['member']});
            }
        },
        onError: (error: any) => {
            console.log(error.message);
        }
    });

    const handleChildDelete = (id) => {
        if(!confirm("자녀정보와 연동된 여권정보가 삭제될 수 있습니다.\n정말로 삭제하시겠습니까?")) {
            return;
        }

        if(children.length <= 1) {
            alert("최소 한 명의 자녀 정보는 입력해야 합니다.");
            return;
        }

        deleteMutation.mutate(id);
    };

    const handleChildModal = (id?: string) => {
        setSelectedChild(id);
        setIsModalOpen(true);
    };

    const handlePassportModal = (id?: string) => {
        setSelectedChild(id);
        setIsPassportModalOpen(true);
    }

    const handlePassportResultModal = (id?: string) => {
        setSelectedChild(id);
        setIsPassportResultModalOpen(true);
    }

    useEffect(() => {
        if (member?.children) {
            setChildren(member.children);
        }
    }, [member]);

    useEffect(() => {
        if (passportList?.data) {
            setPassport(passportList.data);
        }
    }, [passportList]);

	return (
		<section className="space-y-3">
			<div className="flex flex-wrap gap-2 items-baseline">
				<h2 className="text-m text-gray-500 font-bold tracking-tighter">자녀</h2>
                <div className="flex items-center justify-end gap-1 relative group">
                    <div className="relative">
                        <div className="w-4 h-4 rounded-full border border-gray-300 text-gray-400 text-[10px] flex items-center justify-center cursor-pointer bg-white">
                            ?
                        </div>

                        <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden group-hover:block z-10">
                            <div className="relative bg-gray-900 text-white text-[10px] rounded-md px-2 py-1 whitespace-nowrap shadow-lg">
                                자녀는 최대 4명까지 등록 가능합니다.
                                <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                            </div>
                        </div>
                    </div>
                </div>
			</div>
            <div className="flex flex-col gap-4">
                {children?.map((child, index) => {
                    const item = passport?.find((passportItem) => passportItem.childId === child.id);
                    return (
                        <div key={child.id} className="w-full flex md:flex-row flex-col gap-4">
                            {index !== 0 && (
                                <div className="border-t md:hidden"></div>
                            )}
                            <div 
                              onClick={() => handleChildModal(child.id)}
                              className="relative p-5 border rounded-xl bg-gray-50 flex flex-col cursor-pointer hover:border-purple-400 transition-colors group w-full md:w-[50%] min-h-[190px] items-end "
                            >
                                <div className="flex space-x-3 mb-1">
                                    <button 
                                        className="text-xs text-blue-500 hover:text-blue-700 font-medium"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleChildModal(child.id);
                                        }}
                                    >
                                        수정
                                    </button>
                                    <button 
                                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleChildDelete(child.id);
                                        }}
                                    >
                                        삭제
                                    </button>
                                </div>
                                <div className="flex flex-col justify-center w-full text-left ">
                                    <div className="flex-shrink-0 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm mb-3">
                                        <User className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <div className="flex-1 min-w-0 text-left">
                                        <p className="text-[15px] font-bold text-gray-900 truncate">
                                            {child.name || "이름 없음"}
                                        </p>
                                    </div>
                                    <div className="mt-1 space-y-0.5">
                                        <p className="text-xs text-gray-500 flex items-center">
                                            <span className="w-16 text-gray-400 tracking-tighter flex-shrink-0">성별</span>
                                            <span>{child.gender === "M" ? "남자" : child.gender === "F" ? "여자" : "-"}</span>
                                        </p>
                                    </div>
                                    <div className="mt-1 space-y-0.5">
                                        <p className="text-xs text-gray-500 flex items-center">
                                            <span className="w-16 text-gray-400 tracking-tighter flex-shrink-0">생년월일</span>
                                            <span>{child.birth || "-"}</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <PassportSection item={item} childId={child.id} handlePassportModal={handlePassportModal} handlePassportResultModal={handlePassportResultModal}/>
                        </div>
                    );
                })}
            </div>
            {children.length < 4 && (
                <button
                    type="button"
                    onClick={() => handleChildModal(null)}
                    className="w-full border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-2 text-gray-400 hover:bg-gray-50 hover:border-gray-300 transition-colors group"
                >
                    <div className="p-3 rounded-full  transition-transform">
                        <PlusCircle className="w-8 h-8" />
                    </div>
                </button>
            )}
            <Portal>
                {isModalOpen && (
                    <ChildModal
                        id={selectedChild}
                        onClose={() => setIsModalOpen(false)} 
                    />
                )}
            </Portal>
            <Portal>
                {isPassportModalOpen && (
                    <PassportModal
                        id={selectedChild}
                        onClose={() => setIsPassportModalOpen(false)} 
                    />
                )}
            </Portal>
            <Portal>
                {isPassportResultModalOpen && (
                    <PassportResultModal
                        id={selectedChild}
                        onClose={() => setIsPassportResultModalOpen(false)} 
                    />
                )}
            </Portal>
        </section>
	);
}

export default ChildrenSection;